import React, { useState, useEffect, useRef } from 'react';
import styles from './InvoiceGenerator.module.css';
import { getClients } from '../CRM/services/clientService'; 
import { getInvoices, addInvoice, updateInvoice, deleteInvoice } from './services/invService';

const numberToWords = (num) => {
  const a = ['','One ','Two ','Three ','Four ', 'Five ','Six ','Seven ','Eight ','Nine ','Ten ','Eleven ','Twelve ','Thirteen ','Fourteen ','Fifteen ','Sixteen ','Seventeen ','Eighteen ','Nineteen '];
  const b = ['', '', 'Twenty','Thirty','Forty','Fifty', 'Sixty','Seventy','Eighty','Ninety'];

  if ((num = num.toString()).length > 9) return 'overflow';
  let n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{1})(\d{2})(\d{2})(\d{2})$/);
  if (!n) return; let str = '';
  str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
  str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
  str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Lakh ' : '';
  str += (n[4] != 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Thousand ' : '';
  str += (n[5] != 0) ? (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) + 'Hundred ' : '';
  if (n[5] != 0 && n[6] != 0) str += 'and ';
  
  let word = '';
  if (num === 0) return 'Zero';
  
  const numberStr = parseInt(num, 10).toString();
  if(numberStr.length <= 5) {
      const thousands = Math.floor(num / 1000);
      const remainder = num % 1000;
      const hundreds = Math.floor(remainder / 100);
      const tensAndOnes = remainder % 100;

      if(thousands) word += (a[thousands] || b[Math.floor(thousands/10)] + ' ' + a[thousands%10]) + 'Thousand ';
      if(hundreds) word += a[hundreds] + 'Hundred ';
      if(tensAndOnes) word += (a[tensAndOnes] || b[Math.floor(tensAndOnes/10)] + ' ' + a[tensAndOnes%10]);
      return word + 'Rupees Only';
  }

  return num + ' Rupees Only';
};

const InvoiceGenerator = () => {
  const [clients, setClients] = useState([]);
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [showSavedList, setShowSavedList] = useState(false);
  const [currentInvoiceId, setCurrentInvoiceId] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [numInstallments, setNumInstallments] = useState(4);
  
  const defaultInvoiceData = {
    invoiceNo: '0001',
    invoiceDate: new Date().toISOString().split('T')[0],
    reverseCharge: 'N',
    state: 'Uttarakhand',
    companyGST: '',
    accountNumber: '89543777078',
    ifscCode: 'IDFB0021811',
    upiDetails: '7533928185@ybl',
    bankName: 'IDFC FIRST BANK',
    accountHolderName: 'ANVIDE TECH PRIVATE LIMITED',
    bankBranch: 'Haldwani',
    clientName: '',
    clientAddress: '',
    clientGSTIN: '',
    clientState: 'Assam',
    taxRate: 18,
    services: [
      { id: 1, sNo: '1', description: 'Daily 4 AI Videos (15 sec each)\nMonthly Service', sacCode: '998314', taxableValue: 20000, cgstRate: '', cgstAm: 'N/A', sgstRate: '', sgstAm: 'N/A', igstRate: '18%', total: 20000 }
    ],
    advancePayment: 0,
    isInstallments: false,
    installments: []
  };

  const [invoiceData, setInvoiceData] = useState(defaultInvoiceData);
  const invoiceRef = useRef(null);

  useEffect(() => {
    const fetchClients = async () => {
      const data = await getClients();
      setClients(data);
    };
    fetchClients();
    fetchSavedInvoices();
  }, []);

  const fetchSavedInvoices = async () => {
    const data = await getInvoices();
    setSavedInvoices(data);
  };

  const handleClientChange = (e) => {
    const cid = e.target.value;
    setSelectedClientId(cid);
    if (cid === 'custom') {
      setInvoiceData(prev => ({ ...prev, clientName: '', clientAddress: '', clientGSTIN: '', clientState: '' }));
      return;
    }
    const client = clients.find(c => c.id === cid);
    if (client) {
      setInvoiceData(prev => ({
        ...prev,
        clientName: client.clientName || client.name || '',
        clientAddress: client.address || '',
        clientGSTIN: client.gstin || '',
        clientState: client.state || prev.clientState
      }));
    }
  };

  const calculateTotals = () => {
    let totalTaxable = 0;
    invoiceData.services.forEach(s => totalTaxable += Number(s.taxableValue || 0));

    let taxRate = invoiceData.taxRate !== undefined ? Number(invoiceData.taxRate) : 18;
    let totalTax = totalTaxable * (taxRate / 100); 
    let isSameState = invoiceData.state.toLowerCase() === invoiceData.clientState.toLowerCase();
    
    let dueAmount = totalTaxable + totalTax - invoiceData.advancePayment;

    return { totalTaxable, totalTax, dueAmount, isSameState };
  };

  const { totalTaxable, totalTax, dueAmount, isSameState } = calculateTotals();
  const grandTotal = totalTaxable + totalTax;

  useEffect(() => {
    if (invoiceData.isInstallments && numInstallments > 0) {
      const amountPerInst = dueAmount / numInstallments;
      const insts = [];
      const today = new Date();
      const interval = numInstallments > 1 ? Math.floor(30 / numInstallments) : 0;
      
      for(let i=0; i<numInstallments; i++) {
         let d = new Date(today);
         d.setDate(d.getDate() + (i * interval));
         
         const day = d.getDate();
         let suffix = "th";
         if (day % 10 === 1 && day !== 11) suffix = "st";
         else if (day % 10 === 2 && day !== 12) suffix = "nd";
         else if (day % 10 === 3 && day !== 13) suffix = "rd";
         
         insts.push({
            date: `${day}${suffix}`,
            amount: amountPerInst
         });
      }
      setInvoiceData(prev => {
        if (JSON.stringify(prev.installments) !== JSON.stringify(insts)) {
          return { ...prev, installments: insts };
        }
        return prev;
      });
    } else {
      setInvoiceData(prev => {
        if (prev.installments.length !== 0) return { ...prev, installments: [] };
        return prev;
      });
    }
  }, [invoiceData.isInstallments, numInstallments, dueAmount]);

  const handlePrint = () => {
    window.print();
  };

  const handleSave = async () => {
    try {
      if (currentInvoiceId) {
        await updateInvoice(currentInvoiceId, {
          ...invoiceData,
          grandTotal,
          dueAmount
        });
        alert('Invoice Updated Successfully!');
      } else {
        await addInvoice({
          ...invoiceData,
          grandTotal,
          dueAmount
        });
        alert('Invoice Saved Successfully!');
      }
      fetchSavedInvoices();
    } catch (err) {
      alert('Failed to save invoice.');
    }
  };

  const handleEdit = (inv) => {
    setCurrentInvoiceId(inv.id);
    setInvoiceData({
       invoiceNo: inv.invoiceNo || '',
       invoiceDate: inv.invoiceDate || new Date().toISOString().split('T')[0],
       reverseCharge: inv.reverseCharge || 'N',
       state: inv.state || 'Uttarakhand',
       companyGST: inv.companyGST || '',
       accountNumber: inv.accountNumber || '89543777078',
       ifscCode: inv.ifscCode || 'IDFB0021811',
       upiDetails: inv.upiDetails || '7533928185@ybl',
       bankName: inv.bankName || 'IDFC FIRST BANK',
       accountHolderName: inv.accountHolderName || 'ANVIDE TECH PRIVATE LIMITED',
       bankBranch: inv.bankBranch || 'Haldwani',
       clientName: inv.clientName || '',
       clientAddress: inv.clientAddress || '',
       clientGSTIN: inv.clientGSTIN || '',
       clientState: inv.clientState || 'Assam',
       taxRate: inv.taxRate !== undefined ? inv.taxRate : 18,
       services: inv.services || [],
       advancePayment: inv.advancePayment || 0,
       isInstallments: inv.isInstallments || false,
       installments: inv.installments || []
    });
    setNumInstallments(inv.installments && inv.installments.length > 0 ? inv.installments.length : 4);
    setShowSavedList(false);
  };

  const handleDelete = async (id) => {
    if(window.confirm('Are you sure you want to delete this invoice?')) {
       await deleteInvoice(id);
       fetchSavedInvoices();
    }
  };
  
  const handleNewInvoice = () => {
     setCurrentInvoiceId(null);
     setInvoiceData(defaultInvoiceData);
     setNumInstallments(4);
     setShowSavedList(false);
  };

  const handleServiceChange = (index, field, value) => {
    const newServices = [...invoiceData.services];
    newServices[index][field] = value;
    if (field === 'taxableValue') {
       newServices[index].total = Number(value); 
    }
    setInvoiceData({ ...invoiceData, services: newServices });
  };

  const addServiceRow = () => {
    setInvoiceData({
      ...invoiceData,
      services: [...invoiceData.services, { 
        id: Date.now(), 
        sNo: (invoiceData.services.length + 1).toString(), 
        description: '', 
        sacCode: '', 
        taxableValue: 0, 
        cgstRate: '', cgstAm: 'N/A', sgstRate: '', sgstAm: 'N/A', igstRate: '18%', total: 0 
      }]
    });
  };

  return (
    <div className={styles.invoiceContainer}>
      {/* Top action bar hidden in print */}
      <div className={`${styles.formControls} noPrint`} style={{ justifyContent: 'space-between', marginBottom: '10px' }}>
         <h2 style={{ margin: 0, fontSize: '20px' }}>Invoice Generator {currentInvoiceId ? '(Editing)' : '(New)'}</h2>
         <div>
             <button className={styles.btnPrimary} style={{ marginRight: '10px' }} onClick={handleNewInvoice}>+ New Invoice</button>
             <button className={styles.btnSecondary} onClick={() => setShowSavedList(!showSavedList)}>
                {showSavedList ? 'Back to Generator' : 'View Saved Invoices'}
             </button>
         </div>
      </div>

      {showSavedList ? (
         <div className={`${styles.formControls} noPrint`}>
           <h3 style={{ width: '100%', marginBottom: '15px' }}>Saved Invoices</h3>
           <table className={styles.invoiceTable}>
             <thead>
               <tr>
                 <th>Date</th>
                 <th>Invoice #</th>
                 <th>Client</th>
                 <th>Total</th>
                 <th>Actions</th>
               </tr>
             </thead>
             <tbody>
               {savedInvoices.map(inv => (
                 <tr key={inv.id}>
                   <td className={styles.textCenter}>{inv.invoiceDate}</td>
                   <td className={styles.textCenter}>{inv.invoiceNo}</td>
                   <td>{inv.clientName}</td>
                   <td className={styles.textRight}>₹{inv.grandTotal}</td>
                   <td className={styles.textCenter}>
                     <button onClick={() => handleEdit(inv)} className={styles.btnPrimary} style={{ padding: '5px 10px', marginRight: '5px', fontSize: '12px' }}>Edit</button>
                     <button onClick={() => handleDelete(inv.id)} className={styles.btnDanger} style={{ padding: '5px 10px', fontSize: '12px' }}>Delete</button>
                   </td>
                 </tr>
               ))}
               {savedInvoices.length === 0 && <tr><td colSpan="5" className={styles.textCenter}>No saved invoices found.</td></tr>}
             </tbody>
           </table>
         </div>
      ) : (
      <>
      {/* Controls Section - Hidden from print */}
      <div className={`${styles.formControls} noPrint`}>
        <div style={{ width: '100%', borderBottom: '1px solid #ccc', paddingBottom: '10px', marginBottom: '10px', fontWeight: 'bold' }}>Sender & Bank Customization</div>
        <div className={styles.formGroup}>
          <label>Sender State</label>
          <input type="text" value={invoiceData.state} onChange={(e) => setInvoiceData({...invoiceData, state: e.target.value})} />
        </div>
        <div className={styles.formGroup}>
          <label>Company GST</label>
          <input type="text" value={invoiceData.companyGST} onChange={(e) => setInvoiceData({...invoiceData, companyGST: e.target.value})} />
        </div>
        <div className={styles.formGroup}>
          <label>Account Holder Name</label>
          <input type="text" value={invoiceData.accountHolderName} onChange={(e) => setInvoiceData({...invoiceData, accountHolderName: e.target.value})} />
        </div>
        <div className={styles.formGroup}>
          <label>Bank Name</label>
          <input type="text" value={invoiceData.bankName} onChange={(e) => setInvoiceData({...invoiceData, bankName: e.target.value})} />
        </div>
        <div className={styles.formGroup}>
          <label>Bank Branch</label>
          <input type="text" value={invoiceData.bankBranch} onChange={(e) => setInvoiceData({...invoiceData, bankBranch: e.target.value})} />
        </div>
        <div className={styles.formGroup}>
          <label>Account Number</label>
          <input type="text" value={invoiceData.accountNumber} onChange={(e) => setInvoiceData({...invoiceData, accountNumber: e.target.value})} />
        </div>
        <div className={styles.formGroup}>
          <label>IFSC Code</label>
          <input type="text" value={invoiceData.ifscCode} onChange={(e) => setInvoiceData({...invoiceData, ifscCode: e.target.value})} />
        </div>
        <div className={styles.formGroup}>
          <label>UPI Details / Paytm</label>
          <input type="text" value={invoiceData.upiDetails} onChange={(e) => setInvoiceData({...invoiceData, upiDetails: e.target.value})} />
        </div>

        <div style={{ width: '100%', borderTop: '1px solid #ccc', margin: '10px 0' }}></div>
        
        <div className={styles.formGroup}>
          <label>Select Client</label>
          <select value={selectedClientId} onChange={handleClientChange}>
            <option value="">-- Select Client --</option>
            <option value="custom">Custom (Type below)</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.clientName || c.name}</option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Invoice Number</label>
          <input type="text" value={invoiceData.invoiceNo} onChange={(e) => setInvoiceData({...invoiceData, invoiceNo: e.target.value})} />
        </div>
        <div className={styles.formGroup}>
          <label>Invoice Date</label>
          <input type="date" value={invoiceData.invoiceDate} onChange={(e) => setInvoiceData({...invoiceData, invoiceDate: e.target.value})} />
        </div>
        <div className={styles.formGroup}>
          <label>Reverse Charge (Y/N)</label>
          <input type="text" value={invoiceData.reverseCharge} onChange={(e) => setInvoiceData({...invoiceData, reverseCharge: e.target.value})} />
        </div>
        <div className={styles.formGroup}>
          <label>Tax Rate (%)</label>
          <input type="number" value={invoiceData.taxRate !== undefined ? invoiceData.taxRate : 18} onChange={(e) => setInvoiceData({...invoiceData, taxRate: Number(e.target.value)})} />
        </div>
        <div className={styles.formGroup}>
          <label>Advance Payment</label>
          <input type="number" value={invoiceData.advancePayment} onChange={(e) => setInvoiceData({...invoiceData, advancePayment: Number(e.target.value)})} />
        </div>
        
        <div className={styles.installmentsToggle}>
          <input 
            type="checkbox" 
            checked={invoiceData.isInstallments}
            onChange={(e) => setInvoiceData({...invoiceData, isInstallments: e.target.checked})}
          />
          <label>Payment as Installments</label>
        </div>

        {invoiceData.isInstallments && (
          <div className={styles.formGroup} style={{ marginTop: '10px' }}>
            <label>Number of Installments ({numInstallments} Month Duration)</label>
            <input type="number" min="1" max="24" value={numInstallments} onChange={(e) => setNumInstallments(Number(e.target.value))} />
          </div>
        )}

        <div style={{ width: '100%', display: 'flex', gap: '10px', marginTop: '15px' }}>
             <button className={styles.btnSecondary} onClick={addServiceRow}>+ Add Service Row</button>
             <button className={styles.btnPrimary} onClick={handleSave}>{currentInvoiceId ? 'Update Firebase' : 'Save to Firebase'}</button>
             <button className={styles.btnSecondary} onClick={handlePrint} style={{ backgroundColor: '#4b5563' }}>Print / Download PDF</button>
        </div>
      </div>

      {/* Invoice Paper Section - This gets printed */}
      <div className={styles.invoicePaper} ref={invoiceRef}>
        
        {/* Top Details Table */}
        <table className={styles.invoiceTable} style={{ marginBottom: '0' }}>
          <tbody>
            <tr>
              <th colSpan="2" style={{ backgroundColor: '#ccc' }}>Invoice</th>
            </tr>
            <tr>
              <td style={{ width: '50%' }}>
                  {invoiceData.companyGST && <div>Our GSTIN: {invoiceData.companyGST}</div>}
              </td>
              <td style={{ backgroundColor: '#e5e7eb', textAlign: 'center', fontWeight: 'bold' }}>Bill to Party</td>
            </tr>
            <tr>
              <td>Invoice No. : <input className={styles.transparentInput} style={{ width: 'auto' }} value={invoiceData.invoiceNo} readOnly/></td>
              <td>Name: <input className={styles.transparentInput} style={{ width: '80%' }} value={invoiceData.clientName} onChange={(e) => setInvoiceData({...invoiceData, clientName: e.target.value})}/></td>
            </tr>
            <tr>
              <td>Invoice Date : {invoiceData.invoiceDate.split('-').reverse().join('-')}</td>
              <td>Address: <input className={styles.transparentInput} style={{ width: '80%' }} value={invoiceData.clientAddress} onChange={(e) => setInvoiceData({...invoiceData, clientAddress: e.target.value})}/></td>
            </tr>
            <tr>
              <td>Reverse Charge: (Y/N): {invoiceData.reverseCharge}</td>
              <td>GSTIN : <input className={styles.transparentInput} style={{ width: '70%', textTransform: 'uppercase' }} value={invoiceData.clientGSTIN} onChange={(e) => setInvoiceData({...invoiceData, clientGSTIN: e.target.value})}/></td>
            </tr>
            <tr>
              <td>State : {invoiceData.state}</td>
              <td>State : <input className={styles.transparentInput} style={{ width: '70%' }} value={invoiceData.clientState} onChange={(e) => setInvoiceData({...invoiceData, clientState: e.target.value})}/></td>
            </tr>
          </tbody>
        </table>

        {/* Services Table */}
        <table className={styles.invoiceTable} style={{ borderTop: 0 }}>
          <thead>
            <tr>
              <th rowSpan="2">S.No.</th>
              <th rowSpan="2" style={{ width: '30%' }}>Service Description</th>
              <th rowSpan="2">SAC Code</th>
              <th rowSpan="2">Taxable Value</th>
              <th colSpan="2">CGST</th>
              <th colSpan="2">SGST</th>
              <th rowSpan="2">Total</th>
            </tr>
            <tr>
              <th>Rate</th><th>Am.</th>
              <th>Rate</th><th>Am.</th>
            </tr>
          </thead>
          <tbody>
            {invoiceData.services.map((svc, index) => (
              <tr key={svc.id}>
                <td className={styles.borderBottomZero}>{svc.sNo}</td>
                <td className={styles.borderBottomZero}>
                  <textarea 
                    className={styles.transparentInput} 
                    style={{ resize: 'none', height: '40px' }} 
                    value={svc.description}
                    onChange={(e) => handleServiceChange(index, 'description', e.target.value)}
                  />
                </td>
                <td className={styles.borderBottomZero}><input className={styles.transparentInput} value={svc.sacCode} onChange={(e) => handleServiceChange(index, 'sacCode', e.target.value)} /></td>
                <td className={styles.borderBottomZero}><input className={styles.transparentInput} type="number" value={svc.taxableValue} onChange={(e) => handleServiceChange(index, 'taxableValue', e.target.value)} /></td>
                <td className={styles.borderBottomZero}>{isSameState ? `${(invoiceData.taxRate !== undefined ? invoiceData.taxRate : 18) / 2}%` : svc.cgstRate}</td>
                <td className={styles.borderBottomZero}>{isSameState ? (svc.taxableValue * ((invoiceData.taxRate !== undefined ? invoiceData.taxRate : 18) / 200)).toFixed(2) : svc.cgstAm}</td>
                <td className={styles.borderBottomZero}>{isSameState ? `${(invoiceData.taxRate !== undefined ? invoiceData.taxRate : 18) / 2}%` : svc.sgstRate}</td>
                <td className={styles.borderBottomZero}>{isSameState ? (svc.taxableValue * ((invoiceData.taxRate !== undefined ? invoiceData.taxRate : 18) / 200)).toFixed(2) : svc.sgstAm}</td>
                <td className={styles.borderBottomZero}>{svc.taxableValue}</td>
              </tr>
            ))}
            {/* Empty filler rows to match screenshot aesthetic */}
            {[1,2,3].map((_, i) => (
              <tr key={`empty-${i}`}>
                <td className={`${styles.borderTopBottomZero} ${i===2 ? '' : styles.borderBottomZero}`}>&nbsp;</td>
                <td className={`${styles.borderTopBottomZero} ${i===2 ? '' : styles.borderBottomZero}`}></td>
                <td className={`${styles.borderTopBottomZero} ${i===2 ? '' : styles.borderBottomZero}`}></td>
                <td className={`${styles.borderTopBottomZero} ${i===2 ? '' : styles.borderBottomZero}`}></td>
                <td className={`${styles.borderTopBottomZero} ${i===2 ? '' : styles.borderBottomZero}`}></td>
                <td className={`${styles.borderTopBottomZero} ${i===2 ? '' : styles.borderBottomZero}`}></td>
                <td className={`${styles.borderTopBottomZero} ${i===2 ? '' : styles.borderBottomZero}`}></td>
                <td className={`${styles.borderTopBottomZero} ${i===2 ? '' : styles.borderBottomZero}`}></td>
                <td className={`${styles.borderTopBottomZero} ${i===2 ? '' : styles.borderBottomZero}`}></td>
              </tr>
            ))}
            
            {/* Calculation Section */}
            <tr>
              <td colSpan="4" className={styles.textCenter} style={{ backgroundColor: '#e5e7eb', fontWeight: 'bold' }}>Total Invoice amount in words</td>
              <td colSpan="4">Total amount Before Tax</td>
              <td className={styles.textRight}>{totalTaxable.toFixed(2)}</td>
            </tr>
            <tr>
              <td colSpan="4" rowSpan="6" style={{ verticalAlign: 'top', paddingTop: '15px' }}>
                {numberToWords(grandTotal)}
                
                <div style={{ marginTop: '20px' }} className={styles.bankDetailsSection}>
                  <table className={styles.invoiceTable} style={{ width: '100%' }}>
                     <tbody>
                        <tr><td colSpan="2" className={styles.textCenter} style={{ fontWeight: 'bold', fontSize: '16px' }}>Bank Details :</td></tr>
                        <tr><td>A/c Holder's Name</td><td>{invoiceData.accountHolderName}</td></tr>
                        <tr><td>Bank Name</td><td>{invoiceData.bankName}</td></tr>
                        <tr><td>A/c Number</td><td>{invoiceData.accountNumber}</td></tr>
                        <tr><td>IFSC Code.</td><td>{invoiceData.ifscCode}</td></tr>
                        <tr className={styles.paytmRow}><td colSpan="2">Paytm/ UPI : {invoiceData.upiDetails}</td></tr>
                        <tr><td>Bank Branch</td><td>{invoiceData.bankBranch}</td></tr>
                     </tbody>
                  </table>
                </div>
              </td>
              <td colSpan="4">Add: CGST</td>
              <td className={styles.textRight}>{isSameState ? (totalTax / 2).toFixed(2) : 'N/A'}</td>
            </tr>
            <tr>
              <td colSpan="4">Add: SGST</td>
              <td className={styles.textRight}>{isSameState ? (totalTax / 2).toFixed(2) : 'N/A'}</td>
            </tr>
            <tr>
              <td colSpan="4">Add: IGST</td>
              <td className={styles.textRight}>{!isSameState ? `${invoiceData.taxRate !== undefined ? invoiceData.taxRate : 18}%` : 'N/A'}</td>
            </tr>
            <tr>
              <td colSpan="4" style={{ backgroundColor: '#e5e7eb' }}>Total Tax amount</td>
              <td className={styles.textRight} style={{ backgroundColor: '#e5e7eb' }}>{totalTax.toFixed(2)}</td>
            </tr>
            <tr>
              <td colSpan="4">Advance payment</td>
              <td className={styles.textRight}>{invoiceData.advancePayment.toFixed(2)}</td>
            </tr>
            <tr>
              <td colSpan="4" style={{ backgroundColor: '#e5e7eb', fontWeight: 'bold' }}>Due Amount</td>
              <td className={styles.textRight} style={{ backgroundColor: '#e5e7eb', fontWeight: 'bold' }}>{dueAmount.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
         
         <div style={{ marginTop: '10px', fontSize: '10px' }}>ORIGINAL COPY</div>

         {/* Installments Visual in Print/Table */}
         {invoiceData.isInstallments && invoiceData.installments.length > 0 && (
           <div style={{ marginTop: '20px', fontSize: '14px', fontWeight: 'bold' }}>
             <p>Total Invoice Amount: ₹{grandTotal.toLocaleString('en-IN')}</p>
             <p style={{ marginTop: '15px', marginBottom: '10px' }}>Payment to be made in {invoiceData.installments.length} equal installments:</p>
             <div style={{ fontWeight: 'normal' }}>
               {invoiceData.installments.map((inst, idx) => (
                 <p key={idx}>₹{Number(inst.amount).toLocaleString('en-IN')} – Due on {inst.date}</p>
               ))}
             </div>
             <p style={{ marginTop: '15px', fontStyle: 'italic', fontWeight: 'normal' }}>Note: Service will continue based on timely installment payments.</p>
           </div>
         )}

         {/* Signatory */}
         <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: invoiceData.isInstallments ? '10px' : '-80px' }}>
            <div className={styles.signatorySection}>
               {/* Hardcoded signature image from code */}
               <img src="https://res.cloudinary.com/ddkyeuhk8/image/upload/v1774602784/f0zyguci0kqqas0ikqh9.png" alt="Signature" style={{ maxWidth: '150px', maxHeight: '60px', marginBottom: '5px', objectFit: 'contain' }} />
               <div style={{ borderTop: '1px solid #1e3a8a', width: '200px', margin: '0 auto', paddingTop: '5px', fontSize: '14px' }}>
                 Authorized Signatory
               </div>
            </div>
         </div>
         <div style={{ borderBottom: '1px solid #000', marginTop: '40px', marginBottom: '10px' }}></div>

         {/* Footer */}
         <div className={styles.footer}>
            <div>ananta infotech dineshpur uttrakhand </div>
            <div style={{ marginTop: '5px', fontWeight: 'normal' }}>
               Contact No: 7533928185  Email : kishor@anantainfotech.com
            </div>
         </div>

      </div>
      </>
      )}
    </div>
  );
};

export default InvoiceGenerator;
